import type { FC } from 'react'
import { Avatar, Box, IconButton, Typography, type Theme } from '@mui/material'
import AttachFile from '@mui/icons-material/AttachFile'
import Close from '@mui/icons-material/Close'
import Image from '@mui/icons-material/Image'
import type { TranscriptMessage } from './clipboard'
import { getStringInitials } from '~/shared/lib/displayText'
interface MessageQuotePreviewProps {
  messages: TranscriptMessage[]
  onRemove: () => void
}

/**
 * Format a timestamp for display in the quote preview.
 * Example: "Wed. Mar. 25, 2026 at 10:30 am"
 */
function formatTimestamp(isoString: string): string {
  const date = new Date(isoString)
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' })
  const month = date.toLocaleDateString('en-US', { month: 'short' })
  const day = date.getDate()
  const year = date.getFullYear()
  const time = date
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase()
  return `${weekday}. ${month}. ${day}, ${year} at ${time}`
}

/**
 * Blockquote-style preview component for quoted messages in ChatInput.
 * Displays avatar, sender name, timestamp, and styled message bubble.
 */
export const MessageQuotePreview: FC<MessageQuotePreviewProps> = ({ messages, onRemove }) => {
  return (
    <Box
      sx={theme => ({
        position: 'relative',
        borderLeft: `4px solid ${theme.palette.grey['400']}`,
      })}
    >
      <Box
        sx={theme => ({
          padding: theme.spacing(1.5),
          position: 'relative',
          maxHeight: '200px',
          overflowY: 'auto',
        })}
      >
        {/* Quoted messages */}
        {messages.map((msg, index) => (
          <Box key={msg.object_id} sx={{ mb: index < messages.length - 1 ? 2 : 0, pr: 4 }}>
            {/* Row 1: Avatar + Name + Timestamp */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Avatar
                src={msg.avatar_url ?? undefined}
                sx={(theme: Theme) => ({
                  width: 24,
                  height: 24,
                  fontSize: '0.9rem',
                  flexShrink: 0,
                  backgroundColor: msg.is_mine
                    ? theme.palette.primary.main
                    : theme.palette.grey[300],
                  color: msg.is_mine ? theme.palette.common.white : theme.palette.text.primary,
                })}
              >
                {getStringInitials(msg.sender_name)}
              </Avatar>
              <Typography
                variant="body2"
                sx={theme => ({
                  fontWeight: 600,
                  fontSize: '1.3rem',
                  color: theme.palette.text.primary,
                })}
              >
                {msg.sender_name}
              </Typography>
              <Typography
                variant="caption"
                sx={theme => ({
                  color: theme.palette.text.secondary,
                  fontSize: '1.1rem',
                })}
              >
                {formatTimestamp(msg.sent_at)}
              </Typography>
            </Box>

            {/* Row 2: Message content */}
            <Box
              sx={theme => ({
                display: 'inline-block',
                padding: theme.spacing(1, 1.5),
                borderRadius: '12px',
                borderBottomLeftRadius: msg.is_mine ? 12 : 0,
                borderBottomRightRadius: msg.is_mine ? 0 : 12,
                maxWidth: '100%',
                ...(msg.is_mine
                  ? {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.common.white,
                    }
                  : {
                      backgroundColor: theme.palette.grey[100],
                      color: theme.palette.text.primary,
                    }),
              })}
            >
              {msg.text ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '1.3rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {msg.text}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {msg.attachment_type === 'image' ? (
                    <Image sx={{ fontSize: '1.4rem' }} />
                  ) : (
                    <AttachFile sx={{ fontSize: '1.4rem' }} />
                  )}
                  <Typography variant="body2" sx={{ fontSize: '1.3rem' }}>
                    Attachment
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
      {/* Remove button */}
      <IconButton
        onClick={onRemove}
        size="small"
        sx={theme => ({
          position: 'absolute',
          top: 8,
          right: 8,
          padding: '4px',
          backgroundColor: theme.palette.grey[200],
          '&:hover': {
            backgroundColor: theme.palette.grey[300],
          },
        })}
        aria-label="Remove quoted messages"
      >
        <Close sx={{ fontSize: '1.4rem' }} />
      </IconButton>
    </Box>
  )
}
